import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  Drawer,
  List,
  ListItem,
  ListItemText,
  CssBaseline,
  Divider,
  TextField,
  Chip,
  InputAdornment,
  IconButton,
  Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import DescriptionIcon from "@mui/icons-material/Description";
import BadgeIcon from "@mui/icons-material/Badge";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { getAuth, signOut } from "firebase/auth";
import app from "./firebase";

import logo from "../assets/logo-b.png";
import ai_button from "../assets/ai_button_2.gif"; 

const drawerWidth = 240;

const assistants = [
  {
    title: "Generador de Documentos",
    subtitle:
      "Automatiza la creación de tus documentos usando plantillas de Google Docs.",
    icon: <DescriptionIcon />,
    tags: ["Contratos", "Mantenedores"],
    route: "/generador-documentos",
  },
  {
    title: "Cédula de Identidad",
    subtitle:
      "Identifica y procesa datos de cédulas de identidad utilizando IA Generativa.",
    icon: <BadgeIcon />,
    tags: ["Gestion de Postulantes", "Mantenedores"],
    route: "/lector-cedulas",
  },
  {
    title: "Voucher de Asistencia",
    subtitle: "Identifica y procesa datos de Vouchers de asistencia con IA Generativa.",
    icon: <AssignmentIcon />,
    tags: ["Asistencia"],
    route: "/asistencia-voucher",
  },
  {
    title: "Libro de Asistencia",
    subtitle:
      "Identifica y procesa datos de Libros de Asistencia utilizando IA Generativa.",
    icon: <DescriptionIcon />,
    tags: ["Asistencia"],
    route: "/asistencia-libro",
  },
  {
    title: "Registros DT",
    subtitle:
      "Identifica y procesa datos de Registros DT utilizando IA Generativa.",
    icon: <DescriptionIcon />,
    tags: ["Contratos"],
    route: "/registros-dt",
  },
  {
    title: "AFP",
    subtitle: "Utiliza web scraping para obtener la AFP desde Spensiones",
    icon: <SearchIcon />,
    tags: ["Gestion de Postulantes", "Remuneraciones"],
    route: "/afp",
  },
  {
    title: "Carga de documentos a Bizneo",
    subtitle: "Identifica y procesa documentos de trabajadores utilizando esta herramienta.",
    icon: <DescriptionIcon />,
    tags: ["Gestion de Postulantes"],
    route: "/documentos-trabajadores",
  },
  {
    title: "Splitter Documental",
    subtitle: "Divide y procesa documentos PDF",
    icon: <DescriptionIcon />,
    tags: ["Asistencia"],
    route: "/splitter-documental",
  },
];

// SearchWidget
function SearchWidget() {
  const triggerRef = useRef(null);

  useEffect(() => {
    if (triggerRef.current) {
      triggerRef.current.onclick = () => {
        // Aquí puedes agregar lógica adicional para manejar el click si es necesario
      };
    }
  }, []);

  return (
    <div>
      <gen-search-widget
        className="gen-search-widget"
        configId="43509693-ae41-4528-a297-79cb5d66a824"
        triggerId="searchWidgetTrigger"
      ></gen-search-widget>

      <button
        id="searchWidgetTrigger"
        ref={triggerRef}
        className="search-button"
      >
        <img src={ai_button} alt="Buscar" style={{ marginRight: 8 }} />
        Buscador IA
      </button>
    </div>
  );
}

const tagColors = {
  "Gestion de Postulantes": "#1abc9c",
  Mantenedores: "#3498db",
  Asistencia: "#8e44ad",
  Contratos: "#f39c12",
  Remuneraciones: "#e74c3c",
  General: "#2ecc71",
};

const getUniqueTags = (assistants) => {
  const tags = assistants.flatMap((assistant) => assistant.tags);
  return Array.from(new Set(tags)).sort();
};

const normalizeString = (str) => {
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
};

const lightenColor = (color, percent) => {
  const num = parseInt(color.replace("#", ""), 16),
    amt = Math.round(2.55 * percent),
    R = (num >> 16) + amt,
    G = ((num >> 8) & 0x00ff) + amt,
    B = (num & 0x0000ff) + amt;

  return `#${(
    0x1000000 +
    (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
    (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
    (B < 255 ? (B < 1 ? 0 : B) : 255)
  )
    .toString(16)
    .slice(1)
    .toUpperCase()}`;
};

const HomePage = ({ currentUser }) => {
  const [selectedFilter, setSelectedFilter] = useState("Todos");
  const [searchQuery, setSearchQuery] = useState("");
  const triggerRef = useRef(null);

  useEffect(() => {
    if (triggerRef.current) {
      triggerRef.current.onclick = () => {
        // Aquí puedes agregar lógica adicional para manejar el click si es necesario
      };
    }
  }, []);

  const handleClearSearch = () => {
    setSearchQuery("");
  };

  const handleSignOut = () => {
    const auth = getAuth(app);
    signOut(auth)
      .then(() => {
        console.log("Usuario cerró sesión");
      })
      .catch((error) => {
        console.error("Error al cerrar sesión", error);
      });
  };

  const filteredAssistants = assistants.filter((assistant) => {
    const matchesFilter =
      selectedFilter === "Todos" || assistant.tags.includes(selectedFilter);
    const searchQueryNormalized = normalizeString(searchQuery);
    const matchesSearch =
      normalizeString(assistant.title).includes(searchQueryNormalized) ||
      normalizeString(assistant.subtitle).includes(searchQueryNormalized) ||
      assistant.tags.some((tag) =>
        normalizeString(tag).includes(searchQueryNormalized)
      );

    return matchesFilter && matchesSearch;
  });

  const uniqueTags = getUniqueTags(assistants);

  return (
    <Box sx={{ display: "flex", bgcolor: "white" }}>
      <CssBaseline />
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", sm: "block" },
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            boxSizing: "border-box",
          },
        }}
      >
        <Box
          sx={{
            overflow: "auto",
            p: 2,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Box
              sx={{
                position: "relative",
                display: "flex",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  bottom: "-12px",
                  left: "70%",
                  transform: "translateX(-50%)",
                  backgroundColor: "#A3CB3F",
                  color: "#fff",
                  borderRadius: "8px",
                  padding: "2px 8px",
                  fontSize: "0.7rem",
                  fontWeight: "bold",
                  zIndex: 1,
                  width: 80,
                }}
              >
                Labs
              </Box>
              <img
                src={logo}
                alt="Logo"
                style={{
                  maxWidth: "80%",
                  maxHeight: "80px",
                  filter: "brightness(0) invert(1)",
                }}
              />
            </Box>
            <TextField
              fullWidth
              placeholder="Buscar"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton onClick={handleClearSearch} size="small">
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2, bgcolor: "white", borderRadius: 1, color: "black" }}
            />
            <Divider sx={{ mb: 2 }} />
            <List>
              {["Todos", ...uniqueTags].map((text) => (
                <ListItem
                  button
                  key={text}
                  onClick={() => setSelectedFilter(text)}
                  sx={{
                    bgcolor:
                      selectedFilter === text
                        ? "rgba(0, 0, 0, 0.3)"
                        : "transparent",
                    "&:hover": {
                      bgcolor: "rgba(0, 0, 0, 0.2)",
                    },
                  }}
                >
                  <ListItemText primary={text} />
                </ListItem>
              ))}
            </List>
          </Box>
          <Box sx={{ mt: 4 }}>
          <SearchWidget />
        </Box>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSignOut}
            sx={{ mt: 2 }}
          >
            Cerrar Sesión
          </Button>
        </Box>
      </Drawer>
      <Box component="main" sx={{ flexGrow: 1, p: 3, mt: { xs: 0, sm: 0 }, bgcolor: "white" }}>
        <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ mt: 6 }}> 
  Herramientas de Inteligencia Artificial y automatización en etapa
  experimental
</Typography>
          <Typography variant="h5" color="textSecondary">
            para optimizar procesos aprovechando nuevas tecnologías.
          </Typography>
        </Box>
        <Grid container spacing={3}>
          {filteredAssistants.map((assistant, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Link to={assistant.route} style={{ textDecoration: "none" }}>
                <Card
                  sx={{
                    transition: "transform 0.3s",
                    "&:hover": {
                      transform: "scale(1.05)",
                    },
                  }}
                >
                  <CardHeader
                    avatar={
                      <Avatar
                        aria-label={assistant.title}
                        sx={{ bgcolor: "#A3CB3F" }}
                      >
                        {assistant.icon}
                      </Avatar>
                    }
                    title={assistant.title}
                  />
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">
                      {assistant.subtitle}
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      {assistant.tags.map((tag) => (
                        <Chip
                          key={tag}
                          label={tag}
                          size="small"
                          onClick={() => setSelectedFilter(tag)}
                          sx={{
                            mr: 1,
                            bgcolor: tagColors[tag],
                            color: "#fff",
                            cursor: "pointer",
                            "&:hover": {
                              bgcolor: lightenColor(tagColors[tag], 20),
                            },
                          }}
                        />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Link>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default HomePage;
